/* eslint-disable react/no-unescaped-entities */
import { useEffect, useReducer, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Lock } from 'lucide-react';
import { CHECKOUT } from '@/constants/testIds';
import PixelTracker, { fireLinkPixel, injectLinkPixels } from '@/components/PixelTracker';
import { useHideEmergentBadge } from '@/hooks/useHideEmergentBadge';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { getT, resolveLang } from '@/lib/checkoutI18n';
import { useGeoLang } from '@/lib/useGeoLang';
import { getCheckoutKey } from '@/lib/checkoutKey';

const INITIAL = { status: 'polling', info: null, error: null };
function statusReducer(_state, action) {
  if (action.type === 'paid') return { status: 'paid', info: action.payload, error: null };
  if (action.type === 'failed') return { status: 'failed', info: action.payload, error: null };
  if (action.type === 'timeout') return { status: 'timeout', info: action.payload, error: null };
  if (action.type === 'pending') return { status: 'pending', info: action.payload, error: null };
  if (action.type === 'error') return { status: 'error', info: null, error: action.payload };
  if (action.type === 'owner_only') return { status: 'owner_only', info: null, error: null };
  return INITIAL;
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const pi = params.get('pi');
  const [state, dispatch] = useReducer(statusReducer, INITIAL);
  const timerRef = useRef(null);
  const attemptsRef = useRef(0);
  const sawProcessingRef = useRef(false);
  const linkPixelFired = useRef(false);
  useHideEmergentBadge();
  const storeName = state.info?.store?.display_name || '';
  useDocumentTitle(storeName ? `Commande — ${storeName}` : 'Commande');
  const geoLang = useGeoLang();
  const t = getT(resolveLang(state.info?.store?.language, geoLang));

  useEffect(() => {
    if (!pi) {
      dispatch({ type: 'error', payload: 'payment_intent_id manquant' });
      return undefined;
    }
    let mounted = true;
    (async () => {
      while (mounted) {
        try {
          const r = await axios.get(`${API}/public/checkout/status/${pi}`, { headers: { 'X-Checkout-Key': getCheckoutKey(pi) } });
          if (!mounted) return;
          const ps = r.data.payment_status;
          if (ps === 'paid') {
            dispatch({ type: 'paid', payload: r.data });
            // Redirect to Shopify Thank-You page after a short delay so the
            // PixelTracker has time to fire the purchase event first.
            if (r.data.shopify_order_status_url) {
              timerRef.current = setTimeout(() => {
                window.location.replace(r.data.shopify_order_status_url);
              }, 1200);
            }
            return;
          }
          if (ps === 'failed' || ps === 'cancelled') {
            dispatch({ type: 'failed', payload: r.data });
            return;
          }
          // Asynchronous methods (Klarna, Bancontact…) sit in `processing` for a
          // while. That is NOT a failure — the Stripe webhook finalizes the order
          // server-side even if the buyer leaves. Keep polling, then show a
          // reassuring "validation in progress" screen instead of an error.
          if (ps === 'processing') sawProcessingRef.current = true;
          attemptsRef.current += 1;
          if (attemptsRef.current >= 20) {
            dispatch({ type: sawProcessingRef.current ? 'pending' : 'timeout', payload: r.data });
            return;
          }
          await new Promise((res) => { timerRef.current = setTimeout(res, 2000); });
        } catch (e) {
          if (!mounted) return;
          if (e?.response?.status === 403) { dispatch({ type: 'owner_only' }); return; }
          dispatch({ type: 'error', payload: e?.response?.data?.detail || e.message });
          return;
        }
      }
    })();
    return () => {
      mounted = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pi]);

  // Payment links fire their OWN Meta/TikTok Purchase pixel here (on the success
  // page) so it also works for redirect methods (Klarna, iDEAL…) where the buyer
  // leaves the checkout page. Store checkouts keep using <PixelTracker/> below.
  useEffect(() => {
    if (state.status !== 'paid' || linkPixelFired.current) return;
    const pl = state.info?.payment_link;
    if (!pl || (!pl.meta_pixel_id && !pl.tiktok_pixel_id)) return;
    linkPixelFired.current = true;
    injectLinkPixels(pl.meta_pixel_id, pl.tiktok_pixel_id);
    fireLinkPixel('purchase', {
      metaPixelId: pl.meta_pixel_id, tiktokPixelId: pl.tiktok_pixel_id,
      amount: state.info?.amount || 0, currency: (state.info?.currency || 'eur').toUpperCase(),
    });
  }, [state.status, state.info]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6 fade-in">
      {state.status === 'paid' && !state.info?.payment_link && state.info?.store?.id && (
        <PixelTracker
          storeId={state.info.store.id}
          slug={state.info.store.slug}
          event="purchase"
          amount={state.info.amount || 0}
          paymentIntentId={pi}
          metaPixelId={state.info.store.meta_pixel_id}
          tiktokPixelId={state.info.store.tiktok_pixel_id}
          googleTagId={state.info.store.google_tag_id}
          googleLabel={state.info.store.google_conversion_label}
          currency={state.info.currency || 'EUR'}
        />
      )}
      <Card className="card-flat p-10 max-w-lg w-full">
        {state.status === 'polling' && (
          <div className="text-center" data-testid={CHECKOUT.statusBadge}>
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-zinc-400 mb-6" />
            <div className="overline mb-2">{t.verification}</div>
            <h1 className="font-display text-2xl font-bold mb-3">{t.confirming_payment}</h1>
            <p className="text-zinc-600">{t.validating}</p>
          </div>
        )}

        {state.status === 'paid' && (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-emerald-100 border border-emerald-200 flex items-center justify-center mb-6 rounded-md">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <div className="overline text-emerald-600 mb-2">{t.payment_confirmed_overline}</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mb-3" data-testid={CHECKOUT.successMessage}>
              {t.thank_you}
            </h1>
            <p className="text-zinc-600 mb-6">
              {t.payment_processed_pre} <span className="font-mono font-bold">{state.info?.amount?.toFixed(2)} €</span> {t.payment_processed_post}
            </p>
            {state.info?.shopify_order_name && (
              <div className="dashed-divider pt-5 mt-5">
                <div className="overline mb-2">{t.order}</div>
                <div className="font-mono text-lg">{state.info.shopify_order_name}</div>
              </div>
            )}
            {state.info?.shopify_order_status_url && (
              <div className="mt-6 text-sm text-zinc-500 flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t.redirecting}
              </div>
            )}
            {!state.info?.shopify_order_status_url && state.info?.shopify_error && (
              <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-md text-left">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                {t.shopify_failed_pre} <span className="font-mono">{state.info.shopify_error}</span>
              </div>
            )}
            {!state.info?.shopify_order_status_url && (
              <Link to="/" className="block mt-8">
                <Button variant="outline" className="rounded-md">{t.back_home}</Button>
              </Link>
            )}
          </div>
        )}

        {state.status === 'pending' && (
          <div className="text-center" data-testid="checkout-status-pending">
            <div className="w-16 h-16 mx-auto bg-amber-100 border border-amber-200 flex items-center justify-center mb-6 rounded-md">
              <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
            </div>
            <div className="overline text-amber-600 mb-2">{t.pending_overline}</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mb-3">{t.pending_title}</h1>
            <p className="text-zinc-600">
              {state.info?.amount
                ? <>{t.pending_body_amount_pre} <span className="font-mono font-bold">{state.info.amount.toFixed(2)} €</span> </>
                : <>{t.pending_body_no_amount} </>}
              {t.pending_body_rest}
            </p>
            <Link to="/" className="block mt-8">
              <Button variant="outline" className="rounded-md">{t.back_home}</Button>
            </Link>
          </div>
        )}

        {state.status === 'owner_only' && (
          <div className="text-center" data-testid="checkout-owner-only">
            <div className="w-16 h-16 mx-auto bg-zinc-100 border border-zinc-200 flex items-center justify-center mb-6 rounded-md">
              <Lock className="h-8 w-8 text-zinc-600" />
            </div>
            <div className="overline text-zinc-500 mb-2">— {t.owner_only_overline}</div>
            <h1 className="font-display text-2xl font-bold mb-3">{t.owner_only_title}</h1>
            <p className="text-zinc-600">{t.owner_only_text}</p>
            <Link to="/" className="block mt-8">
              <Button variant="outline" className="rounded-md">{t.back_home}</Button>
            </Link>
          </div>
        )}
        {(state.status === 'failed' || state.status === 'error' || state.status === 'timeout') && (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-red-100 border border-red-200 flex items-center justify-center mb-6 rounded-md">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <div className="overline text-red-600 mb-2">— {state.status === 'timeout' ? t.timeout_overline : t.fail_overline}</div>
            <h1 className="font-display text-2xl font-bold mb-3">{t.payment_not_confirmed_title}</h1>
            <p className="text-zinc-600" data-testid={CHECKOUT.errorMessage}>
              {state.error || t.retry_contact}
            </p>
            <Link to="/" className="block mt-8">
              <Button variant="outline" className="rounded-md">{t.back_home}</Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
